import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

/**
 * 参数示例:
 * ```typescript
 * {
 *   "input": {
 *     "prompt": "A beautiful landscape with mountains and lake at sunset",
 *     "negative_prompt": "blurry, low quality"
 *   },
 *   "parameters": {
 *     "size": "1024*1024",
 *     "n": 1,
 *     "seed": 42,
 *     "prompt_extend": true,
 *     "watermark": false
 *   }
 * }
 * ```
 */
export interface AlibabaWanx21T2iTurboParams {
  /**
   * 输入参数，包含文本提示词
   */
  input: {
    /**
     * 正向提示词，描述要生成的图像内容，长度不超过2000字符
     */
    prompt: string;
    /**
     * 负向提示词，描述要避免的图像内容，长度不超过500字符
     */
    negative_prompt?: string;
  };
  /**
   * 生成参数，配置图像生成选项
   */
  parameters?: {
    /**
     * 图像尺寸，格式为'宽*高'，例如'1024*1024'。宽高必须在[512, 1440]范围内，最大分辨率1440*1440
     */
    size?: string;
    /**
     * 生成图像数量，范围1-4
     * @range 1 - 4
     */
    n?: number;
    /**
     * 随机种子，用于生成可重复的结果，范围0-2147483647
     */
    seed?: number;
    /**
     * 是否启用提示词自动扩展，开启后会丰富和优化提示词
     */
    prompt_extend?: boolean;
    /**
     * 是否在生成的图像上添加水印
     */
    watermark?: boolean;
  };
}

export interface AlibabaWanx21T2iTurboResource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * alibaba_wanx21_t2i_turbo
 *
 * Alibaba Wanxiang 2.1 T2I Turbo 图像生成接口，快速将文本提示词转换为高质量图像。
 * 
 * Model: wanx2.1-t2i-turbo
 * Features:
 * - 快速图像生成（Turbo模式）
 * - 支持提示词自动扩展
 * - 可自定义图像尺寸和数量
 * - 支持负向提示词控制
 * - 可选水印功能
 * - 可控随机种子保证可重复性
 *
 * @example
 * ```typescript
 * import { initSeacloud, alibabaWanx21T2iTurbo } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await alibabaWanx21T2iTurbo(
 *   {
 *     "input": {
 *       "prompt": "A beautiful landscape with mountains and lake at sunset",
 *       "negative_prompt": "blurry, low quality"
 *     },
 *     "parameters": {
 *       "size": "1024*1024",
 *       "n": 1,
 *       "seed": 42,
 *       "prompt_extend": true,
 *       "watermark": false
 *     }
 *   }
 * );
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 AlibabaWanx21T2iTurboParams 接口定义
 * @returns 生成的资源列表
 */
export async function alibabaWanx21T2iTurbo(
  params: AlibabaWanx21T2iTurboParams,
): Promise<AlibabaWanx21T2iTurboResource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 3000,
    maxAttempts: 60,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'alibaba_wanx21_t2i_turbo',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: AlibabaWanx21T2iTurboResource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
